import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Crown, Plus } from 'lucide-react';

import { NsKey } from 'enums/NsKey';

import { PlateformeDto } from 'api/plateforme';
import { ReferentialKey } from 'api/referential';
import { useAppDispatch } from 'app/hooks';
import PlateformeCard from 'back-office/features/plateformes-view/PlateformeCard';
import { PlateformesPickerForm } from 'back-office/shared/form/PlateformesPickerForm';
import { ConfirmationOverlay } from 'components/feedback/ConfirmationOverlay/ConfirmationOverlay';
import { toasterService } from 'components/feedback/Toaster/toasterService';
import { DisplayTypeEnum } from 'api/files';
import { GenericColors } from 'api/tenant';
import { Sizes } from 'enums/sizes';
import { getMultipleReferentialsByKeys } from 'features/referentials/slices/getMultipleReferentialsByKeysThunk';
import { useReferentialUtils } from 'features/referentials/hooks/useReferentialUtils';
import { useDialog } from 'hooks/useDialog';
import { plateformesAPI } from 'services/swagger-api';

import {
  PersonneMoralePlateformeDto,
  addPersonneMoralePlateforme,
  getPersonneMoralePlateformes,
  removePersonneMoralePlateforme
} from 'back-office/features/personne-morale-plateformes/api/personneMoralePlateformesApi';

interface Props {
  personneMoraleId: string;
  onCountChange?: (count: number) => void;
}

interface EnrichedPlateforme extends PlateformeDto {
  isPlateformeOwner: boolean;
  dateRattachement: string;
}

/** Rendered under the Plateformes tab of the PM details page: no card and no
    title (carried by the tab), just the description above the content. */
const PlateformesSection: React.FC<{ children: React.ReactNode }> = ({
  children
}) => {
  const { t } = useTranslation(NsKey.PersonneMorale);
  return (
    <section className="flex flex-col gap-5">
      <p className="text-sm text-gray-500">{t('plateformes.description')}</p>
      {children}
    </section>
  );
};

export const PersonneMoraleAssociatedPlateformes: React.FC<Props> = ({
  personneMoraleId,
  onCountChange
}) => {
  const { t } = useTranslation(NsKey.PersonneMorale);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { openDialog } = useDialog();
  const { getReferentialCodeById } = useReferentialUtils();

  const [allPlateformes, setAllPlateformes] = useState<PlateformeDto[]>([]);
  const [pmPlateformes, setPmPlateformes] = useState<
    PersonneMoralePlateformeDto[]
  >([]);
  const [isLoading, setIsLoading] = useState(true);
  const [orgToRemove, setOrgToRemove] = useState<EnrichedPlateforme | null>(
    null
  );

  useEffect(() => {
    dispatch(getMultipleReferentialsByKeys([ReferentialKey.Plateforme]));
  }, [dispatch]);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const [orgsResponse, pmResponse] = await Promise.all([
        plateformesAPI.getPlateformes(),
        getPersonneMoralePlateformes(personneMoraleId)
      ]);
      setAllPlateformes(orgsResponse.plateformes ?? []);
      setPmPlateformes(pmResponse.plateformes ?? []);
      onCountChange?.(pmResponse.plateformes?.length ?? 0);
    } catch (error) {
      console.error('Error fetching plateformes:', error);
      toasterService.showToast({
        message: t('plateformes.loadError'),
        displayType: DisplayTypeEnum.Toaster,
        color: GenericColors.Red
      });
    } finally {
      setIsLoading(false);
    }
  }, [personneMoraleId, onCountChange, t]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const associatedPlateformes = useMemo<EnrichedPlateforme[]>(() => {
    const orgById = new Map(allPlateformes.map(o => [o.id, o]));
    return (
      pmPlateformes
        .map(pmo => {
          const full = orgById.get(pmo.plateformeId);
          if (!full) return null;
          return {
            ...full,
            isPlateformeOwner: pmo.isPlateformeOwner,
            dateRattachement: pmo.dateRattachement
          } as EnrichedPlateforme;
        })
        .filter((o): o is EnrichedPlateforme => o !== null)
        // Plateforme dont la PM est propriétaire en tête.
        .sort(
          (a, b) => Number(b.isPlateformeOwner) - Number(a.isPlateformeOwner)
        )
    );
  }, [allPlateformes, pmPlateformes]);

  const linkPlateformesToPersonneMorale = useCallback(
    async (selectedCodes: string[]) => {
      const selectedIds = selectedCodes
        .map(code => getReferentialCodeById(ReferentialKey.Plateforme, code))
        .filter((id): id is string => !!id);

      const currentIds = new Set(pmPlateformes.map(o => o.plateformeId));
      const selectedSet = new Set(selectedIds);

      const toAdd = selectedIds.filter(id => !currentIds.has(id));
      const toRemove = pmPlateformes
        .filter(o => !selectedSet.has(o.plateformeId))
        .map(o => o.plateformeId);

      for (const id of toAdd) {
        await addPersonneMoralePlateforme(personneMoraleId, id);
      }
      for (const id of toRemove) {
        await removePersonneMoralePlateforme(personneMoraleId, id);
      }

      toasterService.showToast({
        message: t('plateformes.updated'),
        displayType: DisplayTypeEnum.Toaster,
        color: GenericColors.Green
      });
    },
    [getReferentialCodeById, personneMoraleId, pmPlateformes, t]
  );

  const openLinkPlateformesForm = useCallback(() => {
    const currentCodes = associatedPlateformes.map(o => o.code);
    openDialog(
      {
        title: t('plateformes.managePanelTitle'),
        size: Sizes['3xl']
      },
      <PlateformesPickerForm
        initialSelectedCodes={currentCodes}
        onSubmit={linkPlateformesToPersonneMorale}
        onSuccess={fetchData}
      />
    );
  }, [
    associatedPlateformes,
    openDialog,
    linkPlateformesToPersonneMorale,
    fetchData,
    t
  ]);

  const handleRemove = useCallback(async () => {
    if (!orgToRemove) return;
    const res = await removePersonneMoralePlateforme(
      personneMoraleId,
      orgToRemove.id
    );
    if (!res.isSuccess) {
      toasterService.showToast({
        message: res.errorMessage ?? t('plateformes.removeFailed'),
        displayType: DisplayTypeEnum.Toaster,
        color: GenericColors.Red
      });
      return;
    }
    toasterService.showToast({
      message: t('plateformes.removed', { name: orgToRemove.name }),
      displayType: DisplayTypeEnum.Toaster,
      color: GenericColors.Green
    });
    setOrgToRemove(null);
    await fetchData();
  }, [orgToRemove, personneMoraleId, fetchData, t]);

  if (isLoading) {
    return (
      <PlateformesSection>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {[...Array(4)].map((_, index) => (
            <div
              key={index}
              className="h-[200px] rounded-xl bg-gray-200 animate-pulse"
            />
          ))}
        </div>
      </PlateformesSection>
    );
  }

  if (associatedPlateformes.length === 0) {
    return (
      <PlateformesSection>
        <div className="flex flex-col justify-center items-center py-12 text-gray-500">
          <svg
            className="w-16 h-16 mb-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
            />
          </svg>
          <p className="text-lg font-medium mb-4">{t('plateformes.none')}</p>
          <button
            type="button"
            onClick={openLinkPlateformesForm}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-white text-sm font-medium hover:bg-primary/90"
          >
            <Plus className="w-4 h-4" /> {t('plateformes.attach')}
          </button>
        </div>
      </PlateformesSection>
    );
  }

  return (
    <PlateformesSection>
      <div className="grid md:grid-cols-1 lg:grid-cols-2 xl:grid-cols-4 gap-6">
        {associatedPlateformes.map(plateforme => (
          <div
            key={plateforme.id}
            className={
              plateforme.isPlateformeOwner
                ? 'relative rounded-xl ring-2 ring-amber-400 ring-offset-2'
                : undefined
            }
          >
            {plateforme.isPlateformeOwner && (
              <div className="absolute top-3 right-3 z-10 inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold bg-amber-100 text-amber-800 border border-amber-200 shadow-sm">
                <Crown className="w-3 h-3" /> {t('plateformes.owner')}
              </div>
            )}
            <PlateformeCard
              plateforme={plateforme}
              onClick={() =>
                navigate(`/back-office/plateformes/${plateforme.id}`)
              }
              onDelete={() => setOrgToRemove(plateforme)}
            />
          </div>
        ))}

        <div
          onClick={openLinkPlateformesForm}
          className="relative rounded-xl cursor-pointer transition-all duration-200 hover:-translate-y-1 bg-white border-2 border-dashed border-gray-300 hover:border-primary hover:shadow-lg"
        >
          <div className="p-6 h-full flex flex-col items-center justify-center gap-3">
            <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
              <Plus className="w-8 h-8 text-primary" />
            </div>
            <span className="text-sm font-medium text-gray-700">
              {t('plateformes.manage')}
            </span>
          </div>
        </div>
      </div>

      <ConfirmationOverlay
        isOpen={!!orgToRemove}
        onClose={() => setOrgToRemove(null)}
        onConfirm={handleRemove}
        title={t('plateformes.removeTitle')}
        description={
          orgToRemove
            ? t('plateformes.removeConfirm', { name: orgToRemove.name })
            : ''
        }
        confirmLabel={t('members.remove')}
        cancelLabel={t('members.cancel')}
        variant="danger"
      />
    </PlateformesSection>
  );
};
